import React from 'react';
import { Button, Space, Popconfirm, Typography, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, ExperimentOutlined } from '@ant-design/icons';
import type { AiResource } from '../../../types';
import { renderProvider, renderTags, renderEnabled, parseResourceConfig } from '../utils';

const tierLabel: Record<string, string> = {
  small: '小',
  medium: '中',
  large: '大',
};

interface ActionHandlers {
  onEdit: (record: AiResource) => void;
  onDelete: (id: string) => void;
  onTest: (id: string) => void;
}

/**
 * 表格列配置
 */
export const getColumns = ({
  onEdit,
  onDelete,
  onTest,
}: ActionHandlers) => [
  {
    title: '名称',
    dataIndex: 'name',
    key: 'name',
    width: 200,
    render: (value: string, record: AiResource) => (
      <div>
        <div>{value}</div>
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12, maxWidth: 260, display: 'inline-block' }}
          ellipsis={{ tooltip: record.id }}
          copyable={{ text: record.id, tooltips: ['复制ID', '已复制'] }}
        >
          ID: {record.id}
        </Typography.Text>
      </div>
    ),
  },
  {
    title: '提供商',
    dataIndex: 'provider',
    key: 'provider',
    width: 110,
    render: (value: string) => renderProvider(value),
  },
  {
    title: '模型',
    dataIndex: 'model',
    key: 'model',
    width: 160,
    render: (value: string, record: AiResource) => {
      const config = parseResourceConfig(record.config);
      const qwenVersion = config.qwenVersion;
      return (
        <div>
          <div>{value}</div>
          {record.provider === 'qwen' && qwenVersion && (
            <Tag color="orange" style={{ fontSize: 11, marginTop: 2 }}>
              v{qwenVersion}
            </Tag>
          )}
        </div>
      );
    },
  },
  {
    title: '档位',
    dataIndex: 'modelTier',
    key: 'modelTier',
    width: 72,
    render: (v?: string | null) =>
      v ? tierLabel[v] ?? v : <Typography.Text type="secondary">未标注</Typography.Text>,
  },
  {
    title: '端点',
    dataIndex: 'endpoint',
    key: 'endpoint',
    width: 240,
    ellipsis: true,
  },
  {
    title: '优先级',
    dataIndex: 'priority',
    key: 'priority',
    width: 90,
    sorter: (a: AiResource, b: AiResource) => a.priority - b.priority,
  },
  {
    title: '标签',
    dataIndex: 'tags',
    key: 'tags',
    width: 160,
    render: (value?: string) => renderTags(value),
  },
  {
    title: '状态',
    dataIndex: 'enabled',
    key: 'enabled',
    width: 90,
    render: (value: boolean) => renderEnabled(value),
  },
  {
    title: '操作',
    key: 'action',
    width: 150,
    render: (_: any, record: AiResource) => (
      <Space>
        <Button
          type="link"
          size="small"
          icon={<ExperimentOutlined />}
          onClick={() => onTest(record.id)}
        >
          测试
        </Button>
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => onEdit(record)}
          title="编辑"
        />
        <Popconfirm
          title="确定要删除这个资源吗？"
          onConfirm={() => onDelete(record.id)}
        >
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            title="删除"
          />
        </Popconfirm>
      </Space>
    ),
  },
];

/**
 * 表格配置
 */
export const tableConfig = {
  rowKey: 'id' as const,
  pagination: {
    pageSize: 20,
    showSizeChanger: true,
    showTotal: (total: number) => `共 ${total} 条`,
  },
};
